import React, {
  CSSProperties,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { Checkbox, Dimmer, Icon, Loader, Modal, Popup } from "semantic-ui-react";

import { useIntl } from "react-intl";
import { ComponentPropsGetterR } from "react-table-6";

import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";

import MaskedInput from "react-lib/apps/common/MaskedInput";

import { getRequireKeyError } from "react-lib/apps/USER/CardSettingEmployee";

import CardCalculateWithholdingTax from "./CardCalculateWithholdingTax";
import CardEmployeePaymentDetailUX from "./CardEmployeePaymentDetailUX";

import {
  ACTIONS,
  BTN_ACTS,
  MasterData,
  MasterOptions,
  PickedState,
  RequiredFieldType,
  RunSequence,
  SetProp,
  State,
} from "./sequence/EmployeePayment";

import formatComma from "react-lib/utils/formatComma";

// Types
type CardEmployeePaymentDetailProps = Pick<
  PickedState,
  "buttonLoadCheck" | "errorMessage" | "selectedEmployee"
> & {
  setProp: SetProp;
  runSequence: RunSequence;
  EmployeePaymentSequence: State["EmployeePaymentSequence"];
  readOnly?: boolean;
  languageUX: "en" | "th";
  masterData: MasterData;
  masterOptions: MasterOptions;
};

type Styles = Record<"loader", CSSProperties>;

// Constants
const styles: Styles = {
  loader: {
    alignItems: "flex-start",
    background: "transparent",
    left: 0,
    marginLeft: "1px",
    padding: 0,
  },
};

const POPUP_OFFSET: [number, number] = [8, 0];

const CardEmployeePaymentDetail = (props: CardEmployeePaymentDetailProps) => {
  const intl = useIntl();

  const [openModCallWHT, setOpenModCallWHT] = useState<boolean>(false);
  const [showRequiredField, setShowRequiredField] = useState<RequiredFieldType>({});

  // Memo Effect
  const detail = useMemo(
    () => props.EmployeePaymentSequence?.detail,
    [props.EmployeePaymentSequence?.detail]
  );

  const bankBranchOptions = useMemo(() => {
    const bankBranch = props.masterData.bankBranch ?? [];
    const filtered = bankBranch.filter((item) => item.bank === detail?.bank);

    return filtered.map((item) => ({
      key: item.id,
      text: item.name,
      value: item.id,
    }));
  }, [detail, props.masterData.bankBranch]);

  useEffect(() => {
    props.runSequence({ sequence: "EmployeePayment", restart: true });

    return () => {
      props.runSequence({ sequence: "EmployeePayment", clear: true });
    };
  }, []);

  useEffect(() => {
    if (!detail) {
      return;
    }

    // คำนวณ (Salary/Wages + Position Pay + License Allowance + Language Pay + Level skill Pay) - Social Security Deduction Amount  - Provident Fund Amount - Amount
    const salary = detail.amount || "";
    const positionPay = detail.position_pay || "";
    const licenseAllowance = detail.license_allowance || "";
    const languagePay = detail.language_pay || "";
    const skillPay = detail.skill_pay || "";
    const socialSecurityAmount = detail.social_security_amount || "";
    const providentFundAmount = detail.provident_fund_amount || "";
    const taxDeductionAmount = detail.tax_deduction_amount || "";

    // คำนวณเงินเดือนรวม (เงินเดือน + เบี้ยเลี้ยงต่างๆ)
    const grossSalary =
      Number(salary) +
      Number(positionPay) +
      Number(licenseAllowance) +
      Number(languagePay) +
      Number(skillPay);

    // คำนวณเงินเดือนหลังหักเงินสมทบ
    const salaryAfterContributions =
      grossSalary - Number(socialSecurityAmount) - Number(providentFundAmount);

    // คำนวณเงินเดือนสุทธิ (หลังหักภาษี)
    const netSalary = salaryAfterContributions - Number(taxDeductionAmount);

    handleChangeValue(null, { name: "net_payment", value: netSalary.toFixed(2) });
  }, [
    detail?.amount,
    detail?.language_pay,
    detail?.license_allowance,
    detail?.position_pay,
    detail?.provident_fund_amount,
    detail?.skill_pay,
    detail?.social_security_amount,
    detail?.tax_deduction_amount,
  ]);

  useEffect(() => {
    if (props.EmployeePaymentSequence?.filter) {
      props.runSequence({ sequence: "EmployeePayment", action: ACTIONS.SEARCH });
    }
  }, [props.EmployeePaymentSequence?.filter]);

  useEffect(() => {
    if (detail?.bank_branch) {
      const foundBankBranch = bankBranchOptions.find(
        (option) => option.value === detail.bank_branch
      );

      if (!foundBankBranch) {
        handleChangeValue(null, { name: "bank_branch", value: null });
      }
    }
  }, [bankBranchOptions, detail]);

  useEffect(() => {
    const error = props.errorMessage?.[BTN_ACTS.SAVE];

    if (error) {
      setShowRequiredField(error);
    }
  }, [props.errorMessage?.[BTN_ACTS.SAVE]]);

  const handleChangeFilter = useCallback(
    (e: SyntheticEvent | null, data: Record<string, any>) => {
      const name = data.name;
      const value = data.type === "checkbox" ? data.checked : data.value;

      props.setProp("EmployeePaymentSequence.filter", {
        ...props.EmployeePaymentSequence?.filter,
        [name]: value,
      });
    },
    [props.EmployeePaymentSequence?.filter]
  );

  const languageScore = useMemo(() => {
    const options = props.masterOptions.languageScore ?? [];

    return options.map((option) => option.text);
  }, [props.masterOptions.languageScore]);

  const levelSkillScore = useMemo(() => {
    const options = props.masterOptions.levelSkillScore ?? [];

    return options.map((option) => option.text);
  }, [props.masterOptions.levelSkillScore]);

  const bankOptions = useMemo(() => {
    const bankBranch = props.masterData.bankBranch ?? [];

    const optionSet = new Set();
    const uniqueBankItems = bankBranch.filter((item) =>
      optionSet.has(item.bank_code) ? false : optionSet.add(item.bank_code)
    );

    return uniqueBankItems.map((item) => ({
      key: item.bank,
      text: item.bank_name,
      value: item.bank,
    }));
  }, [props.masterData.bankBranch]);

  const formattedWHTAmount = useMemo(
    () => (detail?.whtAmount ? formatComma(Number(detail.whtAmount)) : ""),
    [detail?.whtAmount]
  );

  const affiliateName = useMemo(() => {
    const affiliate: Record<string, any>[] = props.selectedEmployee?.affiliate || [];

    if (!Array.isArray(affiliate) || affiliate.length === 0) {
      return "-";
    }

    return affiliate.map((item) => item.name as string).join(", ");
  }, [props.selectedEmployee?.affiliate]);

  const positionName = useMemo(() => {
    const positionAll = props.masterOptions.positionAll ?? [];

    return (
      positionAll.find((option) => Number(option.value) === props.selectedEmployee?.position)
        ?.text || "-"
    );
  }, [props.masterOptions.positionAll, props.selectedEmployee?.position]);

  const formattedItems = useMemo(
    () =>
      (props.EmployeePaymentSequence?.items ?? []).map((item) => ({
        ...item,
        amount: formatComma(item.amount || ""),
        base_ot: item.base_ot ? formatComma(item.base_ot) : "",
        language_pay: formatComma(item.language_pay || ""),
        license_allowance: formatComma(item.license_allowance || ""),
        position_pay: formatComma(item.position_pay || ""),
        provident_fund_amount: formatComma(item.provident_fund_amount || ""),
        skill_pay: formatComma(item.skill_pay || ""),
        tax_deduction_amount: item.tax_deduction_amount
          ? formatComma(item.tax_deduction_amount)
          : "",
      })),
    [props.EmployeePaymentSequence?.items]
  );

  const loadingCheckbox = useMemo(
    () => (
      <div style={{ position: "relative", width: "fit-content" }}>
        <Checkbox
          checked={props.EmployeePaymentSequence?.filter?.include_inactive}
          label="Include inactive"
          name="include_inactive"
          onChange={handleChangeFilter}
        />
        <Dimmer
          style={styles.loader}
          active={props.buttonLoadCheck?.[BTN_ACTS.SEARCH]}
          size="mini"
          inverted
        >
          <Loader size="mini" inline />
        </Dimmer>
      </div>
    ),
    [handleChangeFilter, props.buttonLoadCheck, props.EmployeePaymentSequence?.filter]
  );

  const handleOpenModCalWHT = () => {
    setOpenModCallWHT(true);
  };

  const handleCloseModCalWHT = () => {
    setOpenModCallWHT(false);
  };

  const handleChangeValue = (e: SyntheticEvent | null, data: Record<string, any>) => {
    let inputValue = data.type === "checkbox" ? data.checked : data.value;

    const name: string = data.name;
    const updatedDetail: any = { ...detail };

    // จำกัดเปอร์เซ็นต์กองทุนสำรองเลี้ยงชีพไม่เกิน 100%
    if (name === "provident_fund_perc") {
      inputValue = Number(inputValue) > 100 ? "100" : inputValue;
    }

    // ฟิลด์ที่ต้องจำกัดเป็นตัวเลขทศนิยม 2 ตำแหน่ง
    const numericFields = [
      "amount",
      "base_ot",
      "language_pay",
      "language_score",
      "license_allowance",
      "position_pay",
      "provident_fund_perc",
      "skill_pay",
      "skill_score",
    ];

    if (numericFields.includes(name)) {
      inputValue = /\d+\.?\d{0,2}/.exec(inputValue as string)?.[0];
    }

    updatedDetail[name] = inputValue;

    if (["amount", "has_social_security"].includes(name)) {
      if (updatedDetail.has_social_security) {
        const socialSecurityRate =
          props.EmployeePaymentSequence?.payrollDetailSetting?.employee_social_security_rate ?? "";
        const maximumAmount =
          props.EmployeePaymentSequence?.payrollDetailSetting?.employee_maximum_amount ?? "";

        // แปลงอัตราเป็นทศนิยม (เช่น 5% = 0.05)
        const rateAsDecimal = Number(socialSecurityRate) / 100;

        // คำนวณจำนวนเงินประกันสังคมจากเงินเดือน
        const calculatedAmount = Number(updatedDetail.amount ?? "") * rateAsDecimal;

        // จำกัดจำนวนเงินประกันสังคมไม่ให้เกินจำนวนสูงสุดที่กำหนด
        const finalAmount = Math.min(Number(maximumAmount), calculatedAmount);

        updatedDetail.social_security_amount = finalAmount.toFixed(2);
      } else {
        updatedDetail.social_security_amount = "";
      }
    }

    // คำนวณจำนวนเงินกองทุนสำรองเลี้ยงชีพ: เงินเดือน × อัตราเงินสะสม (%)
    const calculatedProvidentFundAmount =
      updatedDetail.amount && updatedDetail.provident_fund_perc
        ? (Number(updatedDetail.amount) * Number(updatedDetail.provident_fund_perc)) / 100
        : "";

    updatedDetail.provident_fund_amount = calculatedProvidentFundAmount
      ? calculatedProvidentFundAmount.toFixed(2)
      : "";

    // หากเปิดใช้การคำนวณหัก ณ ที่จ่ายอัตโนมัติ
    if (name === "isAutoWHTCalculation") {
      updatedDetail.tax_deduction_amount = inputValue ? updatedDetail.whtAmount : "0.00";
    }

    // คำนวณใหม่เมื่อบันทึก WHT calculation
    if (name === "whtAmount" && [true, undefined].includes(updatedDetail.isAutoWHTCalculation)) {
      updatedDetail.isAutoWHTCalculation = true;
      updatedDetail.tax_deduction_amount = updatedDetail.whtAmount;
    }

    // หากเลือกการจ่ายเงินเป็นเงินสด ให้ล้างข้อมูลธนาคาร
    if (name === "payment_info" && inputValue === "CASH") {
      updatedDetail.bank = null;
      updatedDetail.bank_account = "";
      updatedDetail.bank_branch = null;

      setShowRequiredField((previousState) => ({
        ...previousState,
        bank: "",
        bank_account: "",
        bank_branch: "",
      }));
    }

    props.setProp("EmployeePaymentSequence.detail", { ...updatedDetail });
  };

  const handleSaveCalWHT = (value: string) => {
    handleChangeValue(null, { name: "whtAmount", value });

    handleCloseModCalWHT();
  };

  const handleClickAdd = () => {
    setShowRequiredField({});

    props.setProp("EmployeePaymentSequence.detail", {});
  };

  const handleGetTrProps: ComponentPropsGetterR = (state, rowInfo) => {
    const originalId = rowInfo?.original?.id;

    return {
      className:
        originalId && originalId === props.EmployeePaymentSequence?.detail?.id
          ? "blueSelectedRow"
          : "",
      onClick: () => {
        if (originalId) {
          setShowRequiredField({});

          props.runSequence({
            sequence: "EmployeePayment",
            action: ACTIONS.SELECT,
            id: originalId,
          });
        }
      },
    };
  };

  const handleSave = () => {
    const paymentDetail = props.EmployeePaymentSequence?.detail;

    const requiredKey = {
      amount: "กรุณาระบุ Salary/Wages", // ตามเงื่อนไข
      end_date: "กรุณาระบุ End Date",
      has_social_security: "กรุณาระบุ Social Security Eligibility",
      net_payment: "กรุณาระบุ Net pay amount",
      payment_info: "กรุณาระบุ Payment Information",
      start_date: "กรุณาระบุ Start Date",
      ...(paymentDetail?.payment_info !== "CASH" && {
        bank: "กรุณาระบุ Bank name",
        bank_account: "กรุณาระบุ Bank Account Number",
        bank_branch: "กรุณาระบุ Bank branch name",
      }),
      tax_deduction_amount: "กรุณาระบุ Amount",
    };

    const error = getRequireKeyError(requiredKey as any, { ...paymentDetail }, intl);

    if (typeof paymentDetail?.has_social_security === "boolean") {
      delete error.has_social_security;
    }

    setShowRequiredField(error);

    if (Object.keys(error).length === 0) {
      props.runSequence({ sequence: "EmployeePayment", action: ACTIONS.SAVE });
    }
  };

  const handleCloseDetail = () => {
    props.setProp("EmployeePaymentSequence.detail", null);
  };

  console.log("CardEmployeePaymentDetail props:", props);

  return (
    <div
      style={{
        height: "calc(100vh - 6.5rem)",
        marginTop: "-1.5rem",
        overflowY: "auto",
        paddingTop: "0.5rem",
      }}
    >
      <CardEmployeePaymentDetailUX
        affiliateName={affiliateName}
        detail={props.EmployeePaymentSequence?.detail}
        employeeFullName={props.selectedEmployee?.full_name || ""}
        filter={props.EmployeePaymentSequence?.filter}
        formattedWHTAmount={formattedWHTAmount}
        loadingCheckbox={loadingCheckbox}
        positionName={positionName}
        readOnly={props.readOnly}
        showRequiredField={showRequiredField}
        tableData={formattedItems}
        languageUX={props.languageUX}
        bankAccountTypeOptions={props.masterOptions.bankAccountType}
        bankBranchOptions={bankBranchOptions}
        bankOptions={bankOptions}
        revenueTaxTypeOptions={props.masterOptions.revenueTaxType}
        salaryPaymentTypeOptions={props.masterOptions.salaryPaymentType}
        getTrProps={handleGetTrProps}
        onChangeFilter={handleChangeFilter}
        onChangeValue={handleChangeValue}
        onClickAdd={handleClickAdd}
        onClickCalWHT={handleOpenModCalWHT}
        onClickCancel={handleCloseDetail}
        bankAccount={
          <MaskedInput
            mask="000-0-00000-0"
            name="bank_account"
            readOnly={props.readOnly}
            value={detail?.bank_account ?? ""}
            onChange={handleChangeValue}
          />
        }
        buttonSave={
          !props.readOnly && (
            <ButtonLoadCheck
              setProp={props.setProp}
              color={props.EmployeePaymentSequence?.detail?.id ? "yellow" : "green"}
              paramKey={BTN_ACTS.SAVE}
              size="medium"
              title={props.EmployeePaymentSequence?.detail?.id ? "EDIT" : "SAVE"}
              buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.SAVE]}
              onClick={handleSave}
            />
          )
        }
        langScoreInfo={
          <Popup
            offset={POPUP_OFFSET}
            position="bottom left"
            content={
              <div style={{ lineHeight: "1.65", minWidth: "max-content", whiteSpace: "pre-line" }}>
                <label>Level Score Allowance (THB)</label>
                <ul style={{ marginLeft: "1.75rem" }}>
                  {languageScore.map((text) => (
                    <li key={text}>{text}</li>
                  ))}
                </ul>
              </div>
            }
            trigger={
              <Icon style={{ margin: "0 -0.25rem 0 0.35rem" }} color="blue" name="info circle" />
            }
          />
        }
        levelSkillScoreInfo={
          <Popup
            offset={POPUP_OFFSET}
            position="bottom left"
            content={
              <div style={{ lineHeight: "1.65", minWidth: "max-content", whiteSpace: "pre-line" }}>
                <label>Level Score Allowance (THB)</label>
                <ul style={{ marginLeft: "1.75rem" }}>
                  {levelSkillScore.map((text) => (
                    <li key={text}>{text}</li>
                  ))}
                </ul>
              </div>
            }
            trigger={
              <Icon style={{ margin: "0 -0.25rem 0 0.35rem" }} color="blue" name="info circle" />
            }
          />
        }
      />

      <Modal
        style={{ width: "auto " }}
        open={openModCallWHT}
        closeOnDimmerClick
        onClose={handleCloseModCalWHT}
      >
        {detail && (
          <CardCalculateWithholdingTax
            detail={detail}
            employeeFullName={props.selectedEmployee?.full_name || ""}
            onClose={handleCloseModCalWHT}
            onSave={handleSaveCalWHT}
          />
        )}
      </Modal>
    </div>
  );
};

CardEmployeePaymentDetail.displayName = "CardEmployeePaymentDetail";

export default React.memo(CardEmployeePaymentDetail);
